import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, X, RefreshCw, ChevronRight, MapPinned, Bell, Star, FileText, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { fr, ar } from 'date-fns/locale';
import { PRE_VISIT_ALLERGY_OPTIONS, PRE_VISIT_MEDICATION_OPTIONS, PRE_VISIT_CONTEXT_OPTIONS } from '../data/preVisitOptions';
import { algeriaDateTimeToInstant, addMinutesToHHMM } from '../lib/algeriaTime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Correctif (idee CGM/ClickDoc) : vrai si le patient a deja renseigne au
// moins une information avant consultation (n'importe quel groupe) — sert
// a afficher le badge "transmis au medecin" et a adapter le libelle du
// bouton ("Compléter" vs "Modifier").
const hasPreVisitInfo = (appointment) => {
  const info = appointment.pre_visit_info;
  if (!info) return false;
  return Boolean(
    (info.allergies_tags && info.allergies_tags.length) || info.allergies_other ||
    (info.medications_tags && info.medications_tags.length) || info.medications_other ||
    (info.visit_context_tags && info.visit_context_tags.length) || info.additional_notes
  );
};

const SPECIALTIES = {
  general: { name_fr: 'Médecine générale', name_ar: 'طب عام' },
  cardiology: { name_fr: 'Cardiologie', name_ar: 'أمراض القلب' },
  dermatology: { name_fr: 'Dermatologie', name_ar: 'الأمراض الجلدية' },
  gynecology: { name_fr: 'Gynécologie', name_ar: 'أمراض النساء' },
  ophthalmology: { name_fr: 'Ophtalmologie', name_ar: 'طب العيون' },
  pediatrics: { name_fr: 'Pédiatrie', name_ar: 'طب الأطفال' },
  dentistry: { name_fr: 'Dentiste', name_ar: 'طب الأسنان' },
  orthopedics: { name_fr: 'Orthopédie', name_ar: 'جراحة العظام' },
  psychiatry: { name_fr: 'Psychiatrie', name_ar: 'الطب النفسي' },
  neurology: { name_fr: 'Neurologie', name_ar: 'الأمراض العصبية' },
  urology: { name_fr: 'Urologie', name_ar: 'المسالك البولية' },
  ent: { name_fr: 'ORL', name_ar: 'أنف أذن حنجرة' },
  radiology: { name_fr: 'Radiologie', name_ar: 'الأشعة' },
  gastroenterology: { name_fr: 'Gastro-entérologie', name_ar: 'أمراض الجهاز الهضمي' },
  pneumology: { name_fr: 'Pneumologie', name_ar: 'أمراض الرئة' },
};

const PatientDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const dateFnsLocale = lang === 'ar' ? ar : fr;

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newDate, setNewDate] = useState(null);
  const [newTime, setNewTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [arrivedAppointments, setArrivedAppointments] = useState({});

  // ✅ NOUVEAU - États pour le système d'avis
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewDoctor, setReviewDoctor] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // Correctif (idee CGM/ClickDoc) : informations avant consultation —
  // cases a cocher (allergies / traitements en cours / motif de la visite)
  // + un champ "autre" en texte libre pour chaque groupe.
  const [preVisitDialogOpen, setPreVisitDialogOpen] = useState(false);
  const [preVisitAppointment, setPreVisitAppointment] = useState(null);
  const [preVisitAllergyTags, setPreVisitAllergyTags] = useState([]);
  const [preVisitAllergiesOther, setPreVisitAllergiesOther] = useState('');
  const [preVisitMedicationTags, setPreVisitMedicationTags] = useState([]);
  const [preVisitMedicationsOther, setPreVisitMedicationsOther] = useState('');
  const [preVisitContextTags, setPreVisitContextTags] = useState([]);
  const [preVisitNotes, setPreVisitNotes] = useState('');
  const [preVisitLoading, setPreVisitLoading] = useState(false);

  // Idee CGM/ClickDoc : espace "Ordonnances" — copies privees des
  // ordonnances versees par les medecins au moment de l'impression
  // (routes/prescriptions.py). Charge une seule fois au montage, comme le
  // reste du tableau de bord ; pas de polling, une ordonnance n'est ajoutee
  // qu'au moment ou un medecin imprime, pas en continu.
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescriptionsLoading, setPrescriptionsLoading] = useState(true);

  // ✅ NOUVEAU (teleconsultation) : rejoindre une consultation video
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoJoinLoading, setVideoJoinLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const [calledAlert, setCalledAlert] = useState(null);
  const [notifList, setNotifList] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const calledAppointmentsRef = useRef(new Set());
  // Correctif : fetchAppointments tourne toutes les 10s via setInterval. Sur
  // une session expirée (401), l'ancien code affichait un toast.error à
  // CHAQUE tick, indéfiniment, au lieu de rediriger une seule fois vers la
  // connexion — un patient qui laissait l'onglet ouvert se retrouvait
  // bombardé de messages d'erreur en boucle.
  const sessionExpiredRef = useRef(false);
  const handleSessionExpired = () => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    toast.error('Votre session a expiré. Veuillez vous reconnecter.');
    navigate('/login', { state: { from: window.location.pathname } });
  };

  const getAuthHeaders = () => {
    // Correctif B7 : le cookie httpOnly porte la session (withCredentials global).
    return {};
  };

  useEffect(() => {
    fetchAppointments();
    fetchPrescriptions();
  }, []);

  // Polling 1 : rafraîchit les RDV toutes les 10 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      if (sessionExpiredRef.current) return;
      fetchAppointments();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Polling 2 : vérifie les notifications toutes les 10 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      if (sessionExpiredRef.current) return;
      checkNotifications();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // ✅ Gère tous les types de notifications patient
  const checkNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patient/notifications`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });

      const notifications = response.data || [];
      setNotifList(notifications);

      const now = Date.now();
      const unreadNotifs = notifications.filter(
        (n) => !n.read && !calledAppointmentsRef.current.has(n.notification_id)
      );

      for (const notif of unreadNotifs) {
        calledAppointmentsRef.current.add(notif.notification_id);

        // Toast uniquement pour les notifs recentes (<5 min) afin d'eviter
        // un re-toast a chaque rechargement de page. La cloche, elle, garde tout.
        const created = notif.created_at ? new Date(notif.created_at).getTime() : 0;
        const isRecent = now - created < 5 * 60 * 1000;

        if (notif.type === 'called') {
          setCalledAlert({
            notification_id: notif.notification_id,
            message: notif.message || t('pdash_your_turn'),
          });
          if (isRecent) {
            toast(t('pdash_doctor_calling_toast'), {
              description: t('pdash_your_turn'),
              duration: 15000,
              style: { background: '#0596DE', color: 'white' },
            });
          }
          fetchAppointments();
        }

        if (notif.type === 'new_appointment_by_doctor' && isRecent) {
          toast(t('pdash_new_apt_toast'), {
            description: notif.message,
            duration: 10000,
            style: { background: '#10B981', color: 'white' },
          });
          fetchAppointments();
        }

        if (notif.type === 'appointment_rescheduled' && isRecent) {
          toast(t('pdash_apt_moved_toast'), {
            description: notif.message,
            duration: 12000,
            style: { background: '#F59E0B', color: 'white' },
          });
          fetchAppointments();
        }

        if (notif.type === 'appointment_cancelled' && isRecent) {
          toast(t('pdash_apt_cancelled_toast'), {
            description: notif.message,
            duration: 12000,
            style: { background: '#EF4444', color: 'white' },
          });
          fetchAppointments();
        }

        if (notif.type === 'appointment_confirmed' && isRecent) {
          toast(t('pdash_apt_confirmed_toast'), {
            description: notif.message,
            duration: 10000,
            style: { background: '#10B981', color: 'white' },
          });
          fetchAppointments();
        }
      }
    } catch (error) {
      if (error.response?.status === 401) handleSessionExpired();
    }
  };

  const unreadCount = notifList.filter((n) => !n.read).length;

  const openNotifs = async () => {
    const willShow = !showNotifs;
    setShowNotifs(willShow);
    if (willShow && unreadCount > 0) {
      const unread = notifList.filter((n) => !n.read);
      setNotifList((prev) => prev.map((n) => ({ ...n, read: true })));
      for (const n of unread) {
        try {
          await axios.put(
            `${API_URL}/api/patient/notifications/${n.notification_id}/read`,
            {},
            { withCredentials: true, headers: getAuthHeaders() }
          );
        } catch (_) {}
      }
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/appointments`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setAppointments(response.data);

      const arrived = {};
      response.data.forEach((apt) => {
        if (apt.waiting_status === 'arrived' || apt.waiting_status === 'in_consultation') {
          arrived[apt.appointment_id] = true;
        }
      });
      setArrivedAppointments(arrived);
    } catch (error) {
      if (error.response?.status === 401) {
        handleSessionExpired();
        return;
      }
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  // Idee CGM/ClickDoc : espace "Ordonnances" — voir routes/prescriptions.py.
  const fetchPrescriptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patient/prescriptions`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setPrescriptions(response.data.prescriptions || []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleSessionExpired();
        return;
      }
      // Correctif : une ordonnance en moins a l'ecran est moins genant qu'un
      // toast d'erreur a chaque chargement du tableau de bord si ce sous-
      // systeme a un souci passager — on echoue silencieusement, la liste
      // reste simplement vide/inchangee.
    } finally {
      setPrescriptionsLoading(false);
    }
  };

  const handleViewPrescription = async (prescriptionId) => {
    try {
      const res = await axios.get(`${API_URL}/api/patient/prescriptions/${prescriptionId}`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      window.open(res.data.url, '_blank');
    } catch (error) {
      if (error.response?.status === 401) {
        handleSessionExpired();
        return;
      }
      toast.error(t('pdash_prescription_err_open'));
    }
  };

  const handleCancel = async () => {
    if (!selectedAppointment) return;
    setActionLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/appointments/${selectedAppointment.appointment_id}/cancel`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success('OK');
      setCancelDialogOpen(false);
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
      // Correctif : en cas d'echec (ex: creneau pris entre-temps par un autre
      // patient), la grille affichait toujours l'ancien etat "disponible" —
      // le patient pouvait retenter le meme creneau et retomber sur la meme
      // erreur sans comprendre pourquoi. On rafraichit la grille et on force
      // une nouvelle selection.
      setNewTime('');
      if (newDate && selectedAppointment?.doctor?.doctor_id) {
        fetchSlots(selectedAppointment.doctor.doctor_id, newDate);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const fetchSlots = async (doctorId, date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/api/doctors/${doctorId}/slots?date=${dateStr}`);
      const fetchedSlots = response.data.slots || [];
      // Correctif : le date-picker autorise la selection du jour meme (seul
      // le passe strict est desactive), mais les creneaux deja passes
      // d'aujourd'hui restaient affiches et cliquables ici (le serveur les
      // rejette bien a la soumission, mais l'affichage est trompeur) — meme
      // filtrage que sur la fiche medecin publique (DoctorProfile.js).
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      if (dateStr === todayStr) {
        const currentTime = format(new Date(), 'HH:mm');
        setAvailableSlots(fetchedSlots.filter((slot) => slot > currentTime));
      } else {
        setAvailableSlots(fetchedSlots);
      }
    } catch {
      setAvailableSlots([]);
    }
  };

  const handleReschedule = async () => {
    if (!selectedAppointment || !newDate || !newTime) return;
    setActionLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/appointments/${selectedAppointment.appointment_id}/reschedule`,
        { date: format(newDate, 'yyyy-MM-dd'), time: newTime },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success('OK');
      setRescheduleDialogOpen(false);
      setSelectedAppointment(null);
      setNewDate(null);
      setNewTime('');
      fetchAppointments();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleArrive = async (appointment) => {
    try {
      await axios.post(
        `${API_URL}/api/appointments/${appointment.appointment_id}/arrive`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success(t('pdash_presence_signaled_toast'));
      setArrivedAppointments((prev) => ({ ...prev, [appointment.appointment_id]: true }));
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('pdash_signal_error'));
    }
  };

  // ✅ NOUVEAU - Soumettre un avis
  const handleSubmitReview = async () => {
    if (!reviewComment.trim()) {
      toast.error(t('pdash_review_comment_required'));
      return;
    }
    setReviewLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/reviews`,
        { doctor_id: reviewDoctor.doctor_id, rating: reviewRating, comment: reviewComment },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success(t('pdash_review_published_toast'));
      setReviewDialogOpen(false);
      setReviewComment('');
      setReviewRating(5);
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('pdash_review_publish_error'));
    } finally {
      setReviewLoading(false);
    }
  };

  // Correctif (idee CGM/ClickDoc) : ouvrir la fenetre "infos avant
  // consultation" en pre-remplissant avec ce que le patient a deja
  // renseigne (le cas echeant), pour permettre de le modifier.
  const handleOpenPreVisit = (appointment) => {
    setPreVisitAppointment(appointment);
    setPreVisitAllergyTags(appointment.pre_visit_info?.allergies_tags || []);
    setPreVisitAllergiesOther(appointment.pre_visit_info?.allergies_other || '');
    setPreVisitMedicationTags(appointment.pre_visit_info?.medications_tags || []);
    setPreVisitMedicationsOther(appointment.pre_visit_info?.medications_other || '');
    setPreVisitContextTags(appointment.pre_visit_info?.visit_context_tags || []);
    setPreVisitNotes(appointment.pre_visit_info?.additional_notes || '');
    setPreVisitDialogOpen(true);
  };

  // ✅ NOUVEAU (teleconsultation) : le jeton de reunion est cree a la demande,
  // juste avant de rejoindre (jamais stocke) — voir daily_video.py et
  // routes/appointments.py (GET /api/appointments/{id}/video-token).
  const handleJoinVideo = async (appointment) => {
    setVideoJoinLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/appointments/${appointment.appointment_id}/video-token`, { withCredentials: true, headers: getAuthHeaders() });
      setVideoUrl(res.data.url);
      setVideoDialogOpen(true);
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('pdash_video_join_error'));
    } finally {
      setVideoJoinLoading(false);
    }
  };

  // Correctif : coche/decoche un code dans une liste de tags (allergies,
  // traitements ou motif de la visite) sans dupliquer la logique 3 fois.
  const togglePreVisitTag = (setter) => (code) => {
    setter((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const handleSubmitPreVisit = async () => {
    if (!preVisitAppointment) return;
    setPreVisitLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/appointments/${preVisitAppointment.appointment_id}/pre-visit-info`,
        {
          allergies_tags: preVisitAllergyTags,
          allergies_other: preVisitAllergiesOther || null,
          medications_tags: preVisitMedicationTags,
          medications_other: preVisitMedicationsOther || null,
          visit_context_tags: preVisitContextTags,
          additional_notes: preVisitNotes || null,
        },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success(t('pdash_pre_visit_saved_toast'));
      setPreVisitDialogOpen(false);
      setPreVisitAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('pdash_pre_visit_save_error'));
    } finally {
      setPreVisitLoading(false);
    }
  };

  const upcomingAppointments = appointments.filter(
    (apt) => ['pending', 'confirmed'].includes(apt.status) && !isBefore(parseISO(apt.date), startOfDay(new Date()))
  );
  // Correctif : un RDV du jour meme qui vient de passer en "no_show" (2h
  // apres l'heure prevue, cf backend/no_show.py) n'est plus "pending"/
  // "confirmed" donc sort de upcomingAppointments, mais isBefore(date,
  // today) reste faux tant que minuit n'est pas passe — sans ce cas
  // explicite il disparaissait des deux onglets jusqu'au lendemain.
  const pastAppointments = appointments.filter(
    (apt) => apt.status === 'completed' || apt.status === 'no_show' || isBefore(parseISO(apt.date), startOfDay(new Date()))
  );
  const cancelledAppointments = appointments.filter((apt) => apt.status === 'cancelled');

  const getSpecName = (id) => {
    const s = SPECIALTIES[id];
    return s ? (lang === 'ar' ? s.name_ar : s.name_fr) : id;
  };

  const CheckCircleIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const AppointmentCard = ({ appointment, showActions = true }) => {
    const doctor = appointment.doctor;
    const specialty = getSpecName(doctor?.specialty);
    const appointmentDate = parseISO(appointment.date);
    const isToday = format(appointmentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    const alreadyArrived =
      arrivedAppointments[appointment.appointment_id] ||
      appointment.waiting_status === 'arrived' ||
      appointment.waiting_status === 'in_consultation';
    const isBeingCalled = appointment.waiting_status === 'in_consultation';
    // Correctif : le bouton ne doit s'activer que 30 min avant le RDV,
    // sinon un patient peut se signaler "arrivé" des heures en avance et
    // polluer la salle d'attente du médecin toute la journée.
    //
    // Correctif (meme bug que deja corrige cote DoctorDashboard.js, voir
    // lib/algeriaTime.js) : `new Date(\`${date}T${heure}:00\`)` sans fuseau
    // est interprete par le navigateur dans SON PROPRE fuseau local, pas
    // forcement celui de l'Algerie. Resultat constate en prod : un patient
    // dont le navigateur n'est pas regle sur l'heure d'Algerie voit le
    // bouton "signaler ma presence" rester bloque sur "disponible a partir
    // de 30 min avant" meme quand il est bel et bien dans cette fenetre en
    // heure d'Algerie (le backend, lui, calcule correctement en UTC+1 fixe -
    // voir routes/waiting_room.py - donc seul l'affichage cote patient etait
    // fausse). `algeriaDateTimeToInstant` fixe le decalage a UTC+1,
    // independamment du fuseau du navigateur.
    const appointmentDateTime = algeriaDateTimeToInstant(appointment.date, appointment.time);
    const msUntilAppointment = appointmentDateTime.getTime() - Date.now();
    // Correctif : au-dela de 30 min de retard, on considere que le patient
    // ne viendra plus — le check-in ne doit plus etre possible non plus.
    const checkInTooLate = msUntilAppointment < -30 * 60 * 1000;
    const canCheckIn = msUntilAppointment <= 30 * 60 * 1000 && !checkInTooLate;
    // Correctif : "isPast" ne regardait que la DATE (isBefore sur
    // startOfDay), jamais l'heure — un RDV confirmé du jour restait affiché
    // "à venir" (badge vert, boutons annuler/déplacer actifs) des heures
    // après son créneau. On se base maintenant sur la date+heure réelle du
    // rendez-vous, comme pour msUntilAppointment ci-dessus.
    const isPast = msUntilAppointment < 0;

    // ✅ NOUVEAU (teleconsultation) : le bouton "Rejoindre" ne doit etre
    // cliquable qu'a partir de 3 minutes avant l'heure du RDV, jamais des
    // jours a l'avance (feedback apres premier test en prod) — et jusqu'a
    // la fin normale de la consultation, pas au-dela. Meme regle cote
    // serveur (voir routes/appointments.py, get_video_token) : ceci n'est
    // qu'un confort d'affichage, pas la seule protection.
    const consultationDurationMin = doctor?.consultation_duration || 30;
    const videoJoinReady = msUntilAppointment <= 3 * 60 * 1000 && msUntilAppointment > -consultationDurationMin * 60 * 1000;
    const videoJoinTooEarly = msUntilAppointment > 3 * 60 * 1000;
    // Correctif : reformater avec date-fns `format(...,'HH:mm')` un Date issu
    // de algeriaDateTimeToInstant redonne l'heure locale du NAVIGATEUR (voir
    // avertissement en tete de lib/algeriaTime.js), pas celle d'Algerie -
    // meme bug que juste au-dessus. addMinutesToHHMM reste en arithmetique
    // pure sur "HH:mm", jamais via un objet Date.
    const videoJoinWindowStartLabel = addMinutesToHHMM(appointment.time, -3);
    const isVideoAppointment = appointment.consultation_type === 'video';

    return (
      <div className={`bg-white rounded-2xl border p-6 hover:shadow-md transition-all ${
        isBeingCalled ? 'border-[#0596DE] shadow-lg ring-2 ring-[#0596DE] ring-opacity-50' : 'border-[#E2E8F0]'
      }`}>

        {isBeingCalled && (
          <div className="mb-4 p-4 bg-[#0596DE] rounded-xl text-white flex items-center gap-3 animate-pulse">
            <Bell className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-bold text-lg">{t('pdash_doctor_calling_toast')}</p>
              <p className="text-white/90 text-sm">{t('pdash_your_turn')}</p>
            </div>
          </div>
        )}

        {/* ✅ Bannière RDV programmé par le médecin */}
        {appointment.created_by === 'doctor' && (
          <div className="mb-4 p-3 bg-[#D1FAE5] border border-[#10B981] rounded-xl text-sm text-[#065F46] flex items-center gap-2">
            <span>📅</span>
            <span>{t('pdash_apt_scheduled_by_doctor')}</span>
          </div>
        )}

        {/* ✅ Bannière RDV déplacé par le médecin */}
        {appointment.rescheduled_by === 'doctor' && (
          <div className="mb-4 p-3 bg-[#FEF3C7] border border-[#F59E0B] rounded-xl text-sm text-[#92400E] flex items-start gap-2">
            <span>🔄</span>
            <span>
              {t('pdash_apt_rescheduled_by_doctor')}{appointment.reschedule_reason ? ` — ${appointment.reschedule_reason}` : ''}.
            </span>
          </div>
        )}

        <div className="flex justify-between items-start mb-4">
          {(() => {
            const pastUnresolved = isPast && appointment.status !== 'completed' && appointment.status !== 'cancelled';
            return (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                appointment.status === 'no_show' ? 'bg-[#F1F5F9] text-[#475569]'
                : appointment.status === 'cancelled' ? 'bg-red-100 text-red-600'
                : appointment.status === 'completed' ? 'bg-[#F1F5F9] text-[#475569]'
                : pastUnresolved ? 'bg-[#F1F5F9] text-[#475569]'
                : appointment.status === 'confirmed' ? 'bg-[#D1FAE5] text-[#10B981]'
                : appointment.status === 'pending' ? 'bg-[#FEF3C7] text-[#F59E0B]'
                : 'bg-[#F1F5F9] text-[#475569]'
              }`}>
                {appointment.status === 'no_show' ? t('patient_no_show')
                  : appointment.status === 'cancelled' ? t('patient_cancelled_status')
                  : appointment.status === 'completed' ? t('patient_completed')
                  : pastUnresolved ? t('patient_past_unresolved')
                  : appointment.status === 'confirmed' ? t('patient_confirmed')
                  : appointment.status === 'pending' ? t('patient_pending')
                  : t('patient_completed')}
              </span>
            );
          })()}
          <div className="text-right">
            <div className="flex items-center gap-2 text-[#0596DE] font-semibold">
              <Calendar className="w-4 h-4" />
              {format(appointmentDate, 'dd MMM yyyy', { locale: dateFnsLocale })}
            </div>
            <div className="flex items-center gap-2 text-[#64748B] mt-1">
              <Clock className="w-4 h-4" />
              {appointment.time}
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-xl bg-[#E6F4FB] flex items-center justify-center flex-shrink-0">
            <span className="text-xl font-bold text-[#0596DE]">
              {doctor?.name?.split(' ').map((n) => n[0]).join('').slice(0, 2) || 'DR'}
            </span>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#0F172A]">{doctor?.name || '-'}</h3>
            <p className="text-[#0596DE] text-sm">{specialty}</p>
            <div className="flex items-center gap-2 text-[#64748B] text-sm mt-1">
              <MapPin className="w-3 h-3" />
              {doctor?.address}
            </div>
          </div>
        </div>

        {appointment.reason && (
          <div className="mt-4 p-3 bg-[#F8FAFC] rounded-lg text-sm text-[#64748B]">
            <strong>{t('patient_reason')}:</strong> {appointment.reason}
          </div>
        )}

        {/* ✅ NOUVEAU (teleconsultation) : bouton visible uniquement pour un
            RDV video pas encore passe — le jeton (voir handleJoinVideo)
            n'est cree qu'au clic, jamais stocke a l'avance. Grise (pas
            cache) avant l'ouverture du creneau (3 min avant le RDV), avec
            l'heure d'ouverture affichee, plutot que de disparaitre sans
            explication. */}
        {appointment.consultation_type === 'video' && !['cancelled', 'completed', 'no_show'].includes(appointment.status) && videoJoinReady && (
          <div className="mt-4">
            <Button
              className="w-full rounded-full bg-[#0596DE] hover:bg-[#0480C4] text-white"
              onClick={() => handleJoinVideo(appointment)}
              disabled={videoJoinLoading}
            >
              <Video className="w-4 h-4 mr-2" />{t('pdash_join_video_btn')}
            </Button>
          </div>
        )}
        {appointment.consultation_type === 'video' && !['cancelled', 'completed', 'no_show'].includes(appointment.status) && videoJoinTooEarly && (
          <div className="mt-4">
            <Button className="w-full rounded-full bg-[#F1F5F9] text-[#94A3B8] cursor-not-allowed" disabled>
              <Video className="w-4 h-4 mr-2" />{t('pdash_join_video_too_early').replace('{time}', videoJoinWindowStartLabel)}
            </Button>
          </div>
        )}

        {/* Correctif (idee CGM/ClickDoc) : le patient peut renseigner en ligne
            des informations utiles au medecin avant la consultation (allergies,
            traitements en cours, motif de la visite) — impossible une fois le
            dossier clos (termine/annule/non honore), meme regle que cote
            backend. */}
        {showActions && !['cancelled', 'completed', 'no_show'].includes(appointment.status) && (
          <div className="mt-4">
            {hasPreVisitInfo(appointment) && (
              <p className="text-xs text-[#10B981] mb-2">{t('pdash_pre_visit_filled_badge')}</p>
            )}
            <Button
              variant="outline"
              className="w-full rounded-full text-[#0596DE] border-[#0596DE] hover:bg-[#E6F4FB]"
              onClick={() => handleOpenPreVisit(appointment)}
            >
              {hasPreVisitInfo(appointment) ? t('pdash_pre_visit_button_done') : t('pdash_pre_visit_button')}
            </Button>
          </div>
        )}

        {/* Correctif (demande Nadjib, 20/09/2026) : pour une teleconsultation,
            le patient est chez lui — lui demander de "signaler sa presence en
            salle d'attente" n'avait aucun sens. Le bloc n'est pas supprime
            pour autant : la salle d'attente du medecin n'affiche QUE les
            patients qui se sont signales (waiting_status "arrived", voir
            routes/waiting_room.py), donc sans ce signal le medecin ne saurait
            pas que son patient video est la. C'est la salle d'attente
            VIRTUELLE, facon Doctolib : meme mecanisme, formulation adaptee. */}
        {showActions && isToday && !isPast && appointment.status !== 'cancelled' && (
          <div className="mt-4 p-4 bg-[#E6F4FB] border border-[#0596DE] rounded-xl">
            <p className="text-sm text-[#0596DE] font-medium mb-3">
              {isVideoAppointment ? t('pdash_video_waiting_room_prompt') : t('pdash_waiting_room_prompt')}
            </p>
            {alreadyArrived ? (
              <div className="flex items-center gap-2 text-[#10B981] font-medium text-sm">
                <CheckCircleIcon />
                {isVideoAppointment ? t('pdash_video_presence_signaled_confirm') : t('pdash_presence_signaled_confirm')}
              </div>
            ) : canCheckIn ? (
              <Button
                onClick={() => handleArrive(appointment)}
                className="w-full bg-[#0596DE] hover:bg-[#047CBA] text-white rounded-xl"
              >
                {isVideoAppointment ? <Video className="w-4 h-4 mr-2" /> : <MapPinned className="w-4 h-4 mr-2" />}
                {isVideoAppointment ? t('pdash_im_ready_video') : t('pdash_im_in_waiting_room')}
              </Button>
            ) : checkInTooLate ? (
              <p className="text-sm text-[#64748B]">
                {isVideoAppointment ? t('pdash_video_checkin_too_late') : t('pdash_checkin_too_late')}
              </p>
            ) : (
              <p className="text-sm text-[#64748B]">
                {isVideoAppointment ? t('pdash_video_checkin_available_prefix') : t('pdash_checkin_available_prefix')} ({appointment.time}).
              </p>
            )}
          </div>
        )}

        {showActions && !isPast && appointment.status !== 'cancelled' && (
          // Correctif : au-dela de 24h avant le RDV, on n'affiche plus les
          // boutons annuler/reprogrammer (le backend les refuse de toute
          // facon) mais un message explicatif, pour eviter au patient une
          // erreur surprise au clic.
          (appointmentDateTime.getTime() - Date.now()) >= 24 * 60 * 60 * 1000 ? (
            <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => {
                  setSelectedAppointment(appointment);
                  setRescheduleDialogOpen(true);
                  setNewDate(null);
                  setNewTime('');
                  setAvailableSlots([]);
                }}
                data-testid={`reschedule-btn-${appointment.appointment_id}`}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('patient_reschedule')}
              </Button>
              <Button
                variant="outline"
                className="flex-1 rounded-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  setSelectedAppointment(appointment);
                  setCancelDialogOpen(true);
                }}
                data-testid={`cancel-btn-${appointment.appointment_id}`}
              >
                <X className="w-4 h-4 mr-2" />
                {t('patient_cancel')}
              </Button>
            </div>
          ) : (
            <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
              <p className="text-sm text-[#64748B]">
                {t('pdash_cancel_reschedule_closed')}
              </p>
            </div>
          )
        )}

        {/* ✅ NOUVEAU - Bouton laisser un avis sur RDV passés */}
        {!showActions && appointment.status !== 'cancelled' && (doctor?.doctor_id || appointment.doctor_id) && (
          <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
            <Button
              variant="outline"
              className="w-full rounded-full text-[#F59E0B] border-[#F59E0B] hover:bg-[#FEF3C7]"
              onClick={() => {
                setReviewDoctor(doctor || { doctor_id: appointment.doctor_id, name: t('pdash_this_doctor') });
                setReviewRating(5);
                setReviewComment('');
                setReviewDialogOpen(true);
              }}
            >
              <Star className="w-4 h-4 mr-2 fill-[#F59E0B]" />
              {t('pdash_leave_review')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {calledAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-[#0596DE] text-white rounded-2xl p-5 shadow-2xl flex items-start gap-4 animate-bounce">
            <div className="text-3xl">📢</div>
            <div className="flex-1">
              <p className="font-bold text-lg">{t('pdash_doctor_calling_alert')}</p>
              <p className="text-white/90 text-sm mt-1">{calledAlert.message}</p>
            </div>
            <button onClick={() => setCalledAlert(null)} className="text-white/70 hover:text-white mt-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="patient-dashboard">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] font-['Outfit']">
              {t('patient_hello')} {user?.name?.split(' ')[0]} !
            </h1>
            <p className="text-[#64748B] mt-1">{t('patient_subtitle')}</p>
          </div>
          <div className="relative flex-shrink-0">
            <button
              onClick={openNotifs}
              className="relative p-2.5 rounded-full bg-white border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-[#475569]" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[11px] font-bold flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifs && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowNotifs(false)} />
                <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-2xl border border-[#E2E8F0] shadow-xl z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#F1F5F9] font-semibold text-[#0F172A]">{t('pdash_notifications')}</div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifList.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-[#94A3B8] text-center">{t('pdash_no_notifications')}</div>
                    ) : (
                      notifList.map((n) => (
                        <div key={n.notification_id} className="px-4 py-3 border-b border-[#F8FAFC] last:border-0">
                          <div className="text-sm font-medium text-[#0F172A]">{n.title || t('pdash_notification_singular')}</div>
                          {n.message && <div className="text-xs text-[#64748B] mt-0.5">{n.message}</div>}
                          {n.created_at && (
                            <div className="text-[11px] text-[#94A3B8] mt-1">
                              {new Date(n.created_at).toLocaleString(lang === 'ar' ? 'ar-DZ-u-nu-latn' : 'fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#0596DE] to-[#047CBA] rounded-2xl p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-1">{t('patient_need_apt')}</h2>
              <p className="text-white/80">{t('patient_find_doc')}</p>
            </div>
            <Button
              variant="secondary"
              className="rounded-full bg-white text-[#0596DE] hover:bg-gray-100"
              onClick={() => navigate('/search')}
              data-testid="search-doctor-btn"
            >
              {t('patient_find_btn')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        <Tabs defaultValue="upcoming" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-white rounded-full p-1 border border-[#E2E8F0]">
            <TabsTrigger value="upcoming" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              {t('patient_upcoming')} ({upcomingAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              {t('patient_history')} ({pastAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="cancelled" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              {t('patient_cancelled')} ({cancelledAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="prescriptions" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              {t('patient_prescriptions')} ({prescriptions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
                    <div className="flex gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gray-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-5 bg-gray-200 rounded w-1/2" />
                        <div className="h-4 bg-gray-200 rounded w-1/3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : upcomingAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E2E8F0]">
                <Calendar className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{t('patient_no_upcoming')}</h3>
                <p className="text-[#64748B] mb-4">{t('patient_no_upcoming_desc')}</p>
                <Button onClick={() => navigate('/search')} className="rounded-full bg-[#0596DE]">{t('patient_find_btn')}</Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {upcomingAppointments.map((apt) => (
                  <AppointmentCard key={apt.appointment_id} appointment={apt} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E2E8F0]">
                <Clock className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A]">{t('patient_no_history')}</h3>
              </div>
            ) : (
              <div className="grid gap-4">
                {pastAppointments.map((apt) => (
                  <AppointmentCard key={apt.appointment_id} appointment={apt} showActions={false} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="cancelled">
            {cancelledAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E2E8F0]">
                <X className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A]">{t('patient_no_cancelled')}</h3>
              </div>
            ) : (
              <div className="grid gap-4">
                {cancelledAppointments.map((apt) => (
                  <AppointmentCard key={apt.appointment_id} appointment={apt} showActions={false} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Idee CGM/ClickDoc : espace "Ordonnances" — copies privees des
              ordonnances versees par les medecins au moment de l'impression
              (routes/prescriptions.py). Pas liees a un rendez-vous precis,
              contrairement aux informations avant consultation. */}
          <TabsContent value="prescriptions">
            {prescriptionsLoading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
                    <div className="flex gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gray-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-5 bg-gray-200 rounded w-1/2" />
                        <div className="h-4 bg-gray-200 rounded w-1/3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : prescriptions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E2E8F0]">
                <FileText className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{t('patient_no_prescriptions')}</h3>
                <p className="text-[#64748B]">{t('patient_no_prescriptions_desc')}</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {prescriptions.map((presc) => (
                  <div key={presc.prescription_id} className="bg-white rounded-2xl p-5 border border-[#E2E8F0] flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-xl bg-[#F0F9FF] flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-[#0596DE]" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-[#0F172A] truncate">Dr. {presc.doctor_name}</h4>
                        <p className="text-sm text-[#64748B]">
                          {SPECIALTIES[presc.doctor_specialty]?.[lang === 'ar' ? 'name_ar' : 'name_fr'] || presc.doctor_specialty}
                          {presc.issued_at ? ` — ${format(parseISO(presc.issued_at), 'dd/MM/yyyy')}` : ''}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewPrescription(presc.prescription_id)}
                      className="rounded-full border-[#0596DE] text-[#0596DE] hover:bg-[#F0F9FF] flex-shrink-0"
                    >
                      {t('patient_prescription_view_btn')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ✅ NOUVEAU - Dialog Avis */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{t('pdash_leave_review')}</DialogTitle>
            <DialogDescription>{t('pdash_review_about')} {reviewDoctor?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm font-medium mb-2">{t('pdash_rating_label')}</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className={`w-8 h-8 cursor-pointer transition-colors ${
                      i <= reviewRating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-[#E2E8F0]'
                    }`}
                    onClick={() => setReviewRating(i)}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">{t('pdash_comment_label')}</p>
              <textarea
                className="w-full border border-[#E2E8F0] rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                rows={4}
                placeholder={t('pdash_describe_experience')}
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>{t('patient_cancel')}</Button>
            <Button onClick={handleSubmitReview} disabled={reviewLoading} className="bg-[#0596DE]">
              {reviewLoading ? t('pdash_publishing') : t('pdash_publish')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Informations avant consultation (idee CGM/ClickDoc) — cases a
          cocher parmi une liste predefinie + un champ "autre" en texte libre
          par groupe (allergies, traitements en cours, motif de la visite). */}
      <Dialog open={preVisitDialogOpen} onOpenChange={setPreVisitDialogOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('pdash_pre_visit_title')}</DialogTitle>
            <DialogDescription>{t('pdash_pre_visit_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2">
            <div>
              <p className="text-sm font-medium mb-2">{t('pdash_pre_visit_allergies_label')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                {PRE_VISIT_ALLERGY_OPTIONS.map((opt) => (
                  <label key={opt.code} className="flex items-center gap-2 text-sm text-[#334155]">
                    <input
                      type="checkbox"
                      className="rounded border-[#E2E8F0] text-[#0596DE] focus:ring-[#0596DE]"
                      checked={preVisitAllergyTags.includes(opt.code)}
                      onChange={() => togglePreVisitTag(setPreVisitAllergyTags)(opt.code)}
                    />
                    {t(opt.labelKey)}
                  </label>
                ))}
              </div>
              <input
                type="text"
                className="w-full border border-[#E2E8F0] rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                maxLength={300}
                placeholder={t('pdash_pre_visit_allergies_other_placeholder')}
                value={preVisitAllergiesOther}
                onChange={(e) => setPreVisitAllergiesOther(e.target.value)}
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-2">{t('pdash_pre_visit_medications_label')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                {PRE_VISIT_MEDICATION_OPTIONS.map((opt) => (
                  <label key={opt.code} className="flex items-center gap-2 text-sm text-[#334155]">
                    <input
                      type="checkbox"
                      className="rounded border-[#E2E8F0] text-[#0596DE] focus:ring-[#0596DE]"
                      checked={preVisitMedicationTags.includes(opt.code)}
                      onChange={() => togglePreVisitTag(setPreVisitMedicationTags)(opt.code)}
                    />
                    {t(opt.labelKey)}
                  </label>
                ))}
              </div>
              <input
                type="text"
                className="w-full border border-[#E2E8F0] rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                maxLength={300}
                placeholder={t('pdash_pre_visit_medications_other_placeholder')}
                value={preVisitMedicationsOther}
                onChange={(e) => setPreVisitMedicationsOther(e.target.value)}
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-2">{t('pdash_pre_visit_context_label')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                {PRE_VISIT_CONTEXT_OPTIONS.map((opt) => (
                  <label key={opt.code} className="flex items-center gap-2 text-sm text-[#334155]">
                    <input
                      type="checkbox"
                      className="rounded border-[#E2E8F0] text-[#0596DE] focus:ring-[#0596DE]"
                      checked={preVisitContextTags.includes(opt.code)}
                      onChange={() => togglePreVisitTag(setPreVisitContextTags)(opt.code)}
                    />
                    {t(opt.labelKey)}
                  </label>
                ))}
              </div>
              <textarea
                className="w-full border border-[#E2E8F0] rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                rows={2}
                maxLength={1000}
                placeholder={t('pdash_pre_visit_context_other_placeholder')}
                value={preVisitNotes}
                onChange={(e) => setPreVisitNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreVisitDialogOpen(false)}>{t('patient_cancel')}</Button>
            <Button onClick={handleSubmitPreVisit} disabled={preVisitLoading} className="bg-[#0596DE]">
              {preVisitLoading ? t('pdash_pre_visit_saving') : t('pdash_pre_visit_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Annulation */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{t('patient_cancel_title')}</DialogTitle>
            <DialogDescription>{t('patient_cancel_desc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>{t('patient_keep')}</Button>
            <Button variant="destructive" onClick={handleCancel} disabled={actionLoading} data-testid="confirm-cancel-btn">
              {actionLoading ? t('patient_cancelling') : t('patient_yes_cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Reprogrammation */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>{t('patient_reschedule_title')}</DialogTitle>
            <DialogDescription>{t('patient_reschedule_desc')}</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6 py-4">
            <div>
              <CalendarComponent
                mode="single"
                selected={newDate}
                onSelect={(date) => {
                  setNewDate(date);
                  setNewTime('');
                  if (date && selectedAppointment?.doctor?.doctor_id)
                    fetchSlots(selectedAppointment.doctor.doctor_id, date);
                }}
                disabled={(date) => isBefore(date, startOfDay(new Date()))}
                locale={dateFnsLocale}
                className="rounded-xl border"
              />
            </div>
            <div>
              <h4 className="font-medium mb-3">{t('patient_available_slots')}</h4>
              {!newDate ? (
                <p className="text-[#64748B] text-sm">{t('patient_select_date')}</p>
              ) : availableSlots.length === 0 ? (
                <p className="text-[#64748B] text-sm">{t('patient_no_slots')}</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {availableSlots.map((slot) => (
                    <Button
                      key={slot}
                      variant={newTime === slot ? 'default' : 'outline'}
                      className={`rounded-lg ${newTime === slot ? 'bg-[#0596DE]' : ''}`}
                      onClick={() => setNewTime(slot)}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>{t('admin_cancel')}</Button>
            <Button
              onClick={handleReschedule}
              disabled={!newDate || !newTime || actionLoading}
              className="bg-[#0596DE]"
              data-testid="confirm-reschedule-btn"
            >
              {actionLoading ? t('patient_rescheduling') : t('patient_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✅ NOUVEAU (teleconsultation) : salle video integree (Daily.co) via
          iframe — l'URL contient un jeton nominatif a courte duree, jamais
          reutilise apres la fermeture de ce dialog. */}
      <Dialog open={videoDialogOpen} onOpenChange={(open) => { setVideoDialogOpen(open); if (!open) setVideoUrl(''); }}>
        <DialogContent className="max-w-4xl bg-white h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-[#E2E8F0]">
            <DialogTitle className="flex items-center gap-2"><Video className="w-5 h-5 text-[#0596DE]" />{t('pdash_video_dialog_title')}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-black">
            {videoUrl && (
              <iframe
                src={videoUrl}
                title="Teleconsultation"
                allow="camera; microphone; fullscreen; display-capture; autoplay"
                className="w-full h-full border-0"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientDashboard;
